import React, { useState, useEffect } from 'react';
import api from '../../services/api';

interface QuickInvoiceModalProps {
    isOpen: boolean;
    onClose: () => void;
    order: {
        id: number;
        order_number: string;
        customer_id: number;
        customer_name: string;
        status?: string;
        items: Array<{
            product_id: number;
            product_name: string;
            product_unit_id?: string | number | null;
            quantity: number;
            unit_price: number;
            tax_rate: number;
            discount_percent: number;
        }>;
        subtotal: number;
        tax_amount: number;
        total_cgst?: number;
        total_sgst?: number;
        total_igst?: number;
        is_inter_state?: boolean;
        discount_amount: number;
        total: number;
    } | null;
}


function QuickInvoiceModal({ isOpen, onClose, order }: QuickInvoiceModalProps) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [locations, setLocations] = useState<any[]>([]);

    const [formData, setFormData] = useState({
        payment_method: 'cash',
        transaction_id: '',
        cheque_number: '',
        invoice_date: new Date().toISOString().split('T')[0],
        invoice_time: new Date().toTimeString().slice(0, 5),
        location_id: '',
        notes: ''
    });

    useEffect(() => {
        if (isOpen) {
            loadLocations();
        }
    }, [isOpen]);

    const loadLocations = async () => {
        try {
            const response = await api.get('/locations');
            const locs = response.data?.data || [];
            setLocations(locs);

            // Auto-select primary location
            const primaryLoc = locs.find((l: any) => l.is_primary) || locs[0];
            if (primaryLoc) {
                setFormData(prev => ({ ...prev, location_id: primaryLoc.id }));
            }
        } catch (err) {
            console.error('Failed to load locations:', err);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!order) {
            console.error('[INVOICE MODAL] No order data available');
            return;
        }

        console.log('[INVOICE MODAL] Creating invoice for order:', order);

        setLoading(true);
        setError(null);

        try {
            const payload = {
                invoice: {
                    customer_id: order.customer_id,
                    order_id: order.id,
                    invoice_date: formData.invoice_date,
                    payment_mode: formData.payment_method,
                    transaction_id: formData.transaction_id,
                    cheque_number: formData.cheque_number,
                    location_id: formData.location_id,
                    notes: formData.notes,
                    status: 'paid',
                    invoice_items_attributes: (order.items || []).map(item => ({
                        product_id: item.product_id,
                        product_unit_id: item.product_unit_id,
                        quantity: item.quantity,
                        unit_price: item.unit_price,
                        tax_rate: item.tax_rate,
                        discount_percent: item.discount_percent,
                        batch_id: (item as any).batch_id,
                        stock_batch_id: (item as any).stock_batch_id || (item as any).batch_id
                    }))
                }
            };

            console.log('[INVOICE MODAL] Payload:', payload);

            const response = await api.post('/invoices', payload);

            if (response.success || response.data?.id || response.id) {
                // Show success notification
                const notification = document.createElement('div');
                notification.className = 'fixed top-20 left-1/2 -translate-x-1/2 z-50 bg-emerald-500 text-white px-6 py-3 rounded-lg shadow-lg font-medium';
                notification.textContent = '✓ Invoice created successfully!';
                document.body.appendChild(notification);

                setTimeout(() => notification.remove(), 3000);

                // Navigate to invoice or close modal
                const invoiceId = response.data?.id || response.id;
                setTimeout(() => {
                    window.location.href = `/invoices/${invoiceId}`;
                }, 1000);
            } else {
                setError(response.message || 'Failed to create invoice. Please try again.');
            }
        } catch (err: any) {
            console.error('Invoice creation error:', err);
            setError(err.message || 'An error occurred. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen || !order) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            {/* Overlay */}
            <div
                className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm transition-opacity"
                onClick={onClose}
            />

            {/* Modal */}
            <div className="flex min-h-full items-center justify-center p-4">
                <div
                    className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl transform transition-all"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header */}
                    <div className="px-6 py-5 border-b border-slate-200 bg-gradient-to-r from-indigo-50 to-white">
                        <div className="flex items-center justify-between">
                            <div>
                                <h2 className="text-2xl font-black text-slate-900 tracking-tight">
                                    📄 Create Invoice
                                </h2>
                                <p className="text-sm text-slate-500 mt-1">
                                    From Order #{order.order_number}
                                </p>
                            </div>
                            <button
                                onClick={onClose}
                                className="w-10 h-10 rounded-xl hover:bg-slate-100 flex items-center justify-center text-slate-400 hover:text-slate-600 transition-colors"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    </div>

                    {/* Content */}
                    <form onSubmit={handleSubmit} className="px-6 py-6 space-y-6">
                        {error && (
                            <div className="bg-rose-50 border-l-4 border-rose-500 p-4 rounded-lg">
                                <p className="text-sm text-rose-800 font-medium">{error}</p>
                            </div>
                        )}

                        {/* Customer Info */}
                        <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center text-indigo-600 font-bold">
                                    {order.customer_name.charAt(0).toUpperCase()}
                                </div>
                                <div>
                                    <p className="text-sm text-slate-500 font-medium">Customer</p>
                                    <p className="font-bold text-slate-900">{order.customer_name}</p>
                                </div>
                            </div>
                        </div>

                        {/* Order Items Summary */}
                        <div className="bg-slate-50 rounded-xl p-4 border border-slate-200 max-h-48 overflow-y-auto">
                            <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Items</p>
                            <div className="space-y-2">
                                {order.items.map((item, idx) => (
                                    <div key={idx} className="flex justify-between items-center text-sm">
                                        <span className="text-slate-700">
                                            <span className="font-semibold">{item.quantity}x</span> {item.product_name}
                                        </span>
                                        <span className="font-bold text-slate-900">
                                            ₹{(item.quantity * item.unit_price).toFixed(2)}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Payment Method */}
                        <div>
                            <label className="block text-sm font-bold text-slate-700 mb-2">
                                Payment Method <span className="text-rose-500">*</span>
                            </label>
                            <select
                                required
                                value={formData.payment_method}
                                onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
                                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 transition-all outline-none bg-white font-medium text-slate-700"
                            >
                                <option value="cash">💵 Cash</option>
                                <option value="credit_card">💳 Credit Card</option>
                                <option value="upi">📱 UPI</option>
                                <option value="bank_transfer">🏦 Bank Transfer</option>
                                <option value="cheque">📝 Cheque</option>
                                <option value="other">Other</option>
                            </select>
                        </div>

                        {/* Conditional Payment Tracking Fields */}
                        {(formData.payment_method === 'upi' || formData.payment_method === 'credit_card') && (
                            <div className="animate-in fade-in slide-in-from-top-2 duration-300">
                                <label className="block text-sm font-bold text-slate-700 mb-2">
                                    Transaction ID <span className="text-rose-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    required
                                    value={formData.transaction_id}
                                    onChange={(e) => setFormData({ ...formData, transaction_id: e.target.value })}
                                    placeholder={`Enter ${formData.payment_method.toUpperCase().replace('_', ' ')} Transaction ID`}
                                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 transition-all outline-none"
                                />
                            </div>
                        )}

                        {formData.payment_method === 'cheque' && (
                            <div className="animate-in fade-in slide-in-from-top-2 duration-300">
                                <label className="block text-sm font-bold text-slate-700 mb-2">
                                    Cheque Number <span className="text-rose-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    required
                                    value={formData.cheque_number}
                                    onChange={(e) => setFormData({ ...formData, cheque_number: e.target.value })}
                                    placeholder="Enter Cheque Number"
                                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 transition-all outline-none"
                                />
                            </div>
                        )}

                        {/* Date & Time */}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-2">
                                    Date <span className="text-rose-500">*</span>
                                </label>
                                <input
                                    type="date"
                                    required
                                    value={formData.invoice_date}
                                    onChange={(e) => setFormData({ ...formData, invoice_date: e.target.value })}
                                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 transition-all outline-none"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-2">
                                    Time
                                </label>
                                <input
                                    type="time"
                                    value={formData.invoice_time}
                                    onChange={(e) => setFormData({ ...formData, invoice_time: e.target.value })}
                                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 transition-all outline-none"
                                />
                            </div>
                        </div>

                        {/* Location */}
                        {locations.length > 0 && (
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-2">
                                    Warehouse Location
                                </label>
                                <select
                                    value={formData.location_id}
                                    onChange={(e) => setFormData({ ...formData, location_id: e.target.value })}
                                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 transition-all outline-none bg-white font-medium text-slate-700"
                                >
                                    <option value="">Select Location</option>
                                    {locations.map(loc => (
                                        <option key={loc.id} value={loc.id}>
                                            {loc.name} {loc.is_primary ? '(Primary)' : ''}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {/* Notes */}
                        <div>
                            <label className="block text-sm font-bold text-slate-700 mb-2">
                                Notes (Optional)
                            </label>
                            <textarea
                                rows={2}
                                value={formData.notes}
                                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                placeholder="Add any additional notes..."
                                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 transition-all outline-none resize-none"
                            />
                        </div>

                        {/* Total Summary */}
                        <div className="bg-gradient-to-br from-indigo-50 to-indigo-100/50 rounded-xl p-5 border border-indigo-200">
                            <div className="space-y-2">
                                <div className="flex justify-between text-sm">
                                    <span className="text-slate-600">Subtotal</span>
                                    <span className="font-semibold text-slate-900">₹{order.subtotal.toFixed(2)}</span>
                                </div>
                                {order.is_inter_state ? (
                                    <div className="flex justify-between text-sm">
                                        <span className="text-slate-600">IGST</span>
                                        <span className="font-semibold text-slate-900">₹{(order.total_igst || 0).toFixed(2)}</span>
                                    </div>
                                ) : (
                                    <>
                                        <div className="flex justify-between text-sm">
                                            <span className="text-slate-600">CGST</span>
                                            <span className="font-semibold text-slate-900">₹{(order.total_cgst || 0).toFixed(2)}</span>
                                        </div>
                                        <div className="flex justify-between text-sm">
                                            <span className="text-slate-600">SGST</span>
                                            <span className="font-semibold text-slate-900">₹{(order.total_sgst || 0).toFixed(2)}</span>
                                        </div>
                                    </>
                                )}

                                {order.discount_amount > 0 && (
                                    <div className="flex justify-between text-sm">
                                        <span className="text-slate-600">Discount</span>
                                        <span className="font-semibold text-rose-600">-₹{order.discount_amount.toFixed(2)}</span>
                                    </div>
                                )}
                                <div className="flex justify-between pt-2 border-t border-indigo-200">
                                    <span className="font-bold text-slate-900">Grand Total</span>
                                    <span className="text-2xl font-black text-indigo-600">₹{order.total.toFixed(2)}</span>
                                </div>
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="flex flex-col sm:flex-row gap-3 pt-4">
                            <button
                                type="button"
                                onClick={onClose}
                                className="flex-1 px-6 py-3 bg-white border-2 border-slate-200 rounded-xl text-sm font-bold text-slate-600 hover:bg-slate-50 transition-all"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={loading}
                                className={`flex-1 px-6 py-3 bg-indigo-600 text-white rounded-xl text-sm font-bold hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-100 flex items-center justify-center gap-2 ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
                            >
                                {loading ? (
                                    <>
                                        <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Creating...
                                    </>
                                ) : (
                                    <>
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                        </svg>
                                        Create Invoice & Print
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}

export default QuickInvoiceModal;
